/* eslint-disable complexity */
/* eslint-disable max-depth */
/* eslint-disable max-len */
/**
* @file 任务编辑弹窗
* @author sunyingying01
*/

import React, {type FC, useCallback, useEffect} from 'react';
import {Modal, App, Form, Input} from 'antd';
import {observer} from 'mobx-react-lite';
import {flowResult} from 'mobx';
import {useLocation, useNavigate} from 'react-router-dom';
import {useRequestErrorHandler} from '@/utils/errorHandler';

import {JobStore} from '@/stores';
import {defaultSelectJobUtil} from '@/modules/selectJobUtil';
import {jobIdSyncService} from '@/utils/JobIdSyncService';
import styles from './index.module.less';

export interface IJobEditModalProps {
    store: IPageStore;
    jobStore: JobStore;
}

export const EditModal: FC<IJobEditModalProps> = observer(props => {
    const {message} = App.useApp();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const isCreate = props.jobStore?.editModalStatus === 'create';
    const isRenameRestricted = !isCreate && props.store.rootStore.userStore.isMigrationRestricted;
    const nameValue = Form.useWatch('name', form);
    const {handleError} = useRequestErrorHandler();

    // 初始化表单值
    useEffect(
        () => {
            if (props.jobStore?.showEditJobModal) {
                form.setFieldsValue({
                    name: props.jobStore?.name || ''
                });
            }
        },
        [props.jobStore?.showEditJobModal, props.jobStore?.name, form]
    );

    // 表单校验规则
    const formRules = {
        name: [
            {
                pattern: /^[\u4e00-\u9fa5a-zA-Z0-9_]+$/,
                message: '名称仅支持常用汉字、字母、数字和下划线'
            }
        ]
    };

    const handleCancel = useCallback(
        () => {
            form.resetFields();
            props.jobStore?.update({
                showEditJobModal: false,
                editModalStatus: 'create'
            });
        },
        [props.jobStore, form]
    );

    /** 弹窗完成回调 */
    const handleOk = useCallback(
        // eslint-disable-next-line max-statements
        async () => {
            if (isRenameRestricted) {
                handleCancel();
                return;
            }
            try {
            // 触发表单校验
                const values = await form.validateFields();
                const jobName = values.name.trim();
                props.jobStore.updadeName(jobName);

                const {jobsListStore} = props.store.projectsListStore;

                if (isCreate) {
                // 创建模式
                    const newJobId = await flowResult(props.jobStore.fetchGreatJob());
                    // 检查是否需要重置 taskRunningStore 取消原有job的请求和轮询
                    const oldSelectedJobId = jobIdSyncService.getJobIdFromUrl();
                    if (oldSelectedJobId !== newJobId && oldSelectedJobId) {
                        await props.store.taskRunningStore.cancelAllJobRequests(oldSelectedJobId); // 取消所有进行中的HTTP请求
                        // 重新初始化 taskRunningStore
                        await props.store.taskRunningStore.initTaskRunningStore(newJobId);
                    }
                    // 设置新任务为选中任务
                    jobIdSyncService.setJobId(newJobId);

                    message.success('任务创建成功！');
                    jobsListStore.filterStatus.status = 'ALL';

                    // 刷新列表
                    await flowResult(jobsListStore.fetchList());


                    // 查找新创建的任务并设置为当前选中
                    const newJob = jobsListStore.jobsList.find((j: JobStore) => j.job_id === newJobId);
                    if (newJob) {
                        await defaultSelectJobUtil(props.store, newJob);

                        // 从充值页创建成功后，自动回到任务对应页面
                        if (location.pathname === '/billing') {
                            const params = new URLSearchParams(location.search);
                            params.set('jobId', newJobId);
                            const targetSearch = `?${params.toString()}`;
                            const targetPath = !props.store.taskRunningStore.taskExecutionStore.isInitialingPage
                                && !newJob.isShowInitialingPage
                                ? `/taskRunning${targetSearch}`
                                : `/index${targetSearch}`;
                            navigate(targetPath);
                        }
                    }

                } else {
                    props.jobStore.setCreateLoading(true);
                    await flowResult(props.jobStore.fetchSaveUpdateJob({name: jobName}, false));

                    message.success('任务更新成功！');
                    jobsListStore.filterStatus.status = 'ALL';
                    await flowResult(jobsListStore.fetchList());
                }

                // 成功后关闭弹窗
                handleCancel();
            }
            catch (error: unknown) {
                if (error instanceof Error) {
                    // 表单校验错误不显示消息，由表单组件自己处理
                    if (!error.message.includes('validation')) {
                        handleError(error);
                    }
                }
            }
            finally {
                props.jobStore.setCreateLoading(false);
            }
        },
        [form, props.jobStore, props.store, isCreate, isRenameRestricted, handleCancel, message,
            location.pathname, location.search, navigate]
    );

    return (
        <Modal
            centered
            destroyOnHidden
            maskClosable={false}
            open={props.jobStore?.showEditJobModal || false}
            width={400}
            title={isCreate ? '创建新任务' : '重命名'}
            className={styles.modal}
            cancelButtonProps={{
                autoInsertSpace: false,
                type: 'default'
            }}
            okButtonProps={{
                autoInsertSpace: false,
                loading: props.jobStore.createLoading,
                disabled: !nameValue?.trim() || isRenameRestricted
            }}
            onCancel={handleCancel}
            onOk={() => form.submit()}
            okText={isCreate ? '创建' : '确定'}
            confirmLoading={props.jobStore.createLoading}
        >
            <span className={styles.tip}>
                为你的优化任务指定一个清晰的名称，以便轻松追踪其结果
            </span>

            <Form
                form={form}
                colon={false}
                requiredMark={false}
                layout="vertical"
                onFinish={handleOk}
                initialValues={{
                    name: props.jobStore?.name || ''
                }}
            >
                <Form.Item
                    name="name"
                    rules={formRules.name}
                    validateTrigger={['onChange', 'onBlur']}
                >
                    <Input
                        autoComplete="off"
                        showCount={false}
                        allowClear={false}
                        placeholder="请输入任务名称"
                        maxLength={25}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
});
